import { ChangeEvent, useState, useTransition } from 'react';

import { startOperation } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import { Package } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  itemVariants,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  useImportHubPaths,
  usePerformanceRouteApi,
} from '../../hooks/useImportHubRouteApi.js';
import { initialSearchParams, useTemplates } from '../../queries/index.js';
import { EmptyState } from '../atoms/EmptyState.jsx';
import { Loader } from '../atoms/Loader.jsx';

type TemplateSelectionModalProps = {
  open: boolean;
  onClose: () => void;
};

export const TemplateSelectionModal = ({
  open,
  onClose,
}: TemplateSelectionModalProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const navigate = usePerformanceRouteApi().useNavigate();
  const paths = useImportHubPaths();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [, startTransition] = useTransition();
  const [searchParams, setSearchParams] = useState(initialSearchParams);
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    startTransition(() => {
      setSearchParams((prev) => ({ ...prev, search: event.target.value }));
    });
  };
  const handleSearchClear = () => {
    setSearchParams((prev) => ({ ...prev, search: '' }));
  };

  const { data, isLoading } = useTemplates(searchParams);

  const handleSubmit = () => {
    // Ended by useImportOperation in the data file wizard.
    startOperation('import-hub.import');
    navigate({
      to: paths.importsNew,
      search: { template: selectedTemplateId as string },
    });
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot size="s">
          <DialogHeader>
            <DialogTitle>{t('Templates')}</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex min-h-[320px] flex-col gap-4">
            <DialogDescription>
              {t('Choose one of your template with which to import the data')}
            </DialogDescription>
            <SearchBar
              value={searchParams.search}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              placeholder={t('Search for template')}
              size="small"
              className="min-h-8 w-full"
            />
            <div className="flex h-[300px] items-center">
              {isLoading ? (
                <Loader />
              ) : (
                <div className="h-full flex-1 overflow-auto">
                  <RadioGroup
                    value={selectedTemplateId ?? ''}
                    onValueChange={setSelectedTemplateId}
                    className="flex flex-col gap-2"
                  >
                    {data.items.map((template) => (
                      <Label
                        key={template.id}
                        variant="bodyShort01"
                        data-dd-action-name="Template name option"
                        className={cn(
                          itemVariants({
                            selectionListItem:
                              selectedTemplateId === template.id
                                ? 'selected'
                                : 'default',
                          }),
                          'm-0 cursor-pointer shadow-sm',
                        )}
                      >
                        <RadioGroupItem value={template.id} />
                        <span>{template.name}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                  {!data.items.length && searchParams.search && (
                    <EmptyState
                      text={t('No results found for "{{searchValue}}"', {
                        searchValue: searchParams.search,
                      })}
                      description={t(
                        'Try adjusting your search in order to find what you are looking for.',
                      )}
                      className="flex-1 border-none shadow-none"
                    />
                  )}
                </div>
              )}
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              data-dd-action-name="Template selection: cancel"
              size="large"
              variant="text"
              onClick={onClose}
            >
              {t('Cancel')}
            </Button>
            <Button
              data-dd-action-name="Template selection: import data"
              size="large"
              onClick={handleSubmit}
              disabled={!selectedTemplateId}
            >
              <Package className="size-4" />
              {t('Import data')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};
